import DatePickerLib from 'react-datepicker'
import { createPortal } from 'react-dom'
import { getWidgetPortalContainer } from '@/shared/utils/widgetPortal'
import 'react-datepicker/dist/react-datepicker.css'

export interface DatePickerProps {
  label?: string
  error?: string
  value?: string
  onChange?: (date: string) => void
  min?: string
  max?: string
  disabled?: boolean
  placeholder?: string
  filterWeekends?: boolean
  disablePast?: boolean
  allowedDays?: number[] // 0=Sunday, 1=Monday, ...
  excludedDates?: string[]
  includedDates?: string[]
  required?: boolean
  popperClassName?: string
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

const getYears = (minYear: number, maxYear: number) => {
  const years = []
  for (let i = minYear; i <= maxYear; i++) {
    years.push(i)
  }
  return years
}

const parseDateValue = (value?: string) => {
  if (!value) return null

  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  const slashMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  const parts = isoMatch
    ? {
        year: Number(isoMatch[1]),
        month: Number(isoMatch[2]),
        day: Number(isoMatch[3])
      }
    : slashMatch
      ? {
          year: Number(slashMatch[3]),
          month: Number(slashMatch[1]),
          day: Number(slashMatch[2])
        }
      : null

  if (!parts) return null

  const date = new Date(parts.year, parts.month - 1, parts.day)
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== parts.year ||
    date.getMonth() !== parts.month - 1 ||
    date.getDate() !== parts.day
  ) {
    return null
  }

  return date
}

export const DatePicker = ({
  label,
  error,
  value,
  onChange,
  min,
  max,
  disabled,
  placeholder = 'Select a date',
  filterWeekends = false,
  disablePast = false,
  allowedDays,
  excludedDates,
  includedDates,
  required = false,
  popperClassName = 'datepicker-popper'
}: DatePickerProps) => {
  const hasError = !!error
  const selectedDate = parseDateValue(value)
  const minDate = parseDateValue(min) ?? undefined
  const maxDate = parseDateValue(max) ?? undefined
  const excludedDateValues = excludedDates
    ?.map(parseDateValue)
    .filter((date): date is Date => date !== null)
  const includedDateValues = includedDates
    ?.map(parseDateValue)
    .filter((date): date is Date => date !== null)

  const currentYear = new Date().getFullYear()
  const minYear = minDate ? minDate.getFullYear() : currentYear - 100
  const maxYear = maxDate ? maxDate.getFullYear() : currentYear + 20
  const years = getYears(minYear, maxYear)

  const handleChange = (date: Date | null) => {
    if (date && onChange) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      onChange(`${year}-${month}-${day}`)
    }
  }

  const isWeekday = (date: Date) => {
    const day = date.getDay()
    return day !== 0 && day !== 6
  }

  const isAllowedDay = (date: Date) => {
    if (allowedDays) {
      return allowedDays.includes(date.getDay())
    }
    return true
  }

  const isNotPast = (date: Date) => {
    if (!disablePast) return true
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date >= today
  }

  const filterDate = (date: Date) => {
    let ok = true
    if (filterWeekends) ok = ok && isWeekday(date)
    if (allowedDays && !includedDateValues?.length) ok = ok && isAllowedDay(date)
    if (disablePast) ok = ok && isNotPast(date)
    return ok
  }

  const renderCustomHeader = ({
    date,
    changeYear,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled
  }: {
    date: Date
    changeYear: (year: number) => void
    decreaseMonth: () => void
    increaseMonth: () => void
    prevMonthButtonDisabled: boolean
    nextMonthButtonDisabled: boolean
  }) => (
    <div className="datepicker-header mb-2 px-2">
      <button
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className="datepicker-header__nav"
        aria-label="Previous month"
      >
        ‹
      </button>
      <div className="datepicker-header__controls">
        <span className="datepicker-header__month-button" aria-live="polite">
          {MONTHS[date.getMonth()]}
        </span>
        <select
          value={date.getFullYear()}
          onChange={({ target: { value } }) => changeYear(Number(value))}
          className="datepicker-header__year-select"
        >
          {years.map(year => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        className="datepicker-header__nav"
        aria-label="Next month"
      >
        ›
      </button>
    </div>
  )

  return (
    <div className={`flex flex-col w-full ${label ? 'mb-5' : ''}`}>
      {label && (
        <label className={`enrollment-field-label ${hasError ? '!text-error' : ''}`}>
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <DatePickerLib
        selected={selectedDate}
        onChange={handleChange}
        minDate={minDate}
        maxDate={maxDate}
        excludeDates={excludedDateValues}
        includeDates={includedDateValues}
        filterDate={filterDate}
        disabled={disabled}
        placeholderText={placeholder}
        className={`enrollment-field-control cursor-pointer ${hasError ? 'enrollment-field-control--error' : ''}`}
        calendarClassName="datepicker-calendar"
        popperClassName={popperClassName}
        dateFormat="MM/dd/yyyy"
        showPopperArrow={false}
        renderCustomHeader={renderCustomHeader}
        popperPlacement="bottom-start"
        popperContainer={({ children }) =>
          typeof document === 'undefined'
            ? children
            : createPortal(children, getWidgetPortalContainer() ?? document.body)
        }
      />
      {error && <span className="text-xs text-error mt-1.5 flex items-center gap-1">{error}</span>}
    </div>
  )
}
